import { shouldShowTrialByCancerType } from 'components/Sponsor/constants/cancerTypeConditions';
import { checkIfSponsoredTrialTemplateExists } from 'modules/sponsoredTrialTemplate/api';
import { TEMPLATE_CONTENT_BLOCK_NAMES } from 'components/Sponsor/constants';
import { nctNumberToNctId } from 'functions/formValidations/nctNumberToNctId';
// eslint-disable-next-line import/no-cycle
import analytics from 'functions/analytics';
import { downloadSponsorPDF, downloadTrialsPDF, getFormattedProfile } from './api';
import { MatchFlow } from './matchFlow';
import { MATCH_FLOWS } from './constants';

const MONTH_IN_MS = 2.628e+9;

export const getMatchFlow = ({
  requestToRematch, profileCreatedAt, profileUpdatedAt, matchesProfileUpdatedAt,
}) => {
  // MatchFlow.getMatchFlow() === MATCH_FLOWS.BASIC does not guarantee that profile was updated in the questionnaire
  // it is true in the case a user just skipped a question
  const isIntentionToRematch = requestToRematch || MatchFlow.getMatchFlow() === MATCH_FLOWS.BASIC;

  if (!isIntentionToRematch) {
    return MATCH_FLOWS.LATEST;
  }

  // DEV-1422:
  // do rematch if:
  // - profile updated
  // - or within 30 days of registration

  // Note: location update is not reflected in profile.personal.profile_updated_at
  // lng, lat are passed to getting matches request
  if (new Date(profileCreatedAt).getTime() + MONTH_IN_MS < Date.now()) {
    // recently (within 30 days) registered
    return MATCH_FLOWS.BASIC;
  }

  // fresh profile.personal.profile_updated_at does not guarantee that profile was updated
  // as it can be affected (value updated but profile actually in not changed) in many cases for e.g.:
  // -- HasAnythingChanged popup (isAnythingChangeUpdate)
  // -- questionnaire question is skipped

  if (!matchesProfileUpdatedAt || new Date(matchesProfileUpdatedAt).getTime() < new Date(profileUpdatedAt).getTime()) {
    return MATCH_FLOWS.BASIC;
  }

  return MATCH_FLOWS.LATEST;
};

const getFirstSponsoredTrial = (trials) => (trials ?? []).find((trial) => trial?.isSponsored);

export const downloadTrialsPdf = async ({ trials, userData, pdfFor }) => {
  const isSingleSelectedTrial = trials?.length === 1;

  const singleSelectedSponsoredTrial = isSingleSelectedTrial ? getFirstSponsoredTrial(trials) : null;

  let singleSelectedTrialNctNumber;
  if (singleSelectedSponsoredTrial) {
    singleSelectedTrialNctNumber = singleSelectedSponsoredTrial._id;
  }

  if (
    isSingleSelectedTrial
    && singleSelectedTrialNctNumber
    && shouldShowTrialByCancerType(userData.personalCondition, singleSelectedTrialNctNumber)
    && getFirstSponsoredTrial(trials)
  ) {
    const {
      userWixId, profileId, country, personalCondition, lang,
    } = userData;

    const { name: countryName, state: countryState } = country ?? {};

    const conditions = {
      lang,
      isMobile: false,
      isForPDF: true,
      country: countryName,
      cancerType: personalCondition,
      nctNumbers: [singleSelectedTrialNctNumber],
    };
    if (countryState) {
      conditions.countryStates = [countryState];
    }

    let shouldDownloadSponsorPDF = false;
    try {
      let success;
      ({ result: shouldDownloadSponsorPDF, success = true } = await checkIfSponsoredTrialTemplateExists(
        nctNumberToNctId(singleSelectedTrialNctNumber),
        conditions,
        TEMPLATE_CONTENT_BLOCK_NAMES.DESCRIPTION,
      ));
      if (!success) {
        throw new Error('checkIfSponsoredTrialTemplateExists response.success is negative');
      }
    } catch (ex) {
      console.error(ex);
      return;
    }

    if (shouldDownloadSponsorPDF) {
      analytics.clickDownloadAndShareOnMinisite();

      await downloadSponsorPDF(singleSelectedTrialNctNumber, {
        profileId,
        userWixId,
        countryState,
        nctNumber: singleSelectedTrialNctNumber,
        conditions,
        indication: personalCondition,
      });
      return;
    }
  }

  // the case of not sponsored trial(s)
  await downloadTrialsPDF({
    cancerType: userData?.personalCondition,
    providerCode: userData?.providerCode,
    pdfFor,
  });
};

export const printProfilePDF = (lang) => {
  const getPrintIframe = () => {
    const existedIframe = document.getElementById('iframe_for_print');
    if (existedIframe) {
      return existedIframe;
    }
    const iframe = document.createElement('iframe');
    iframe.id = 'iframe_for_print';
    document.body.appendChild(iframe);
    iframe.style.display = 'none';
    return iframe;
  };
  const printIframe = getPrintIframe();
  return getFormattedProfile(true, lang)
    .then((data) => {
      const file = new Blob([data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      printIframe.src = fileURL;
      printIframe.onload = () => {
        setTimeout(() => {
          printIframe.focus();
          printIframe.contentWindow.print();
          printIframe.onload = () => {
          };
        }, 1);
      };
    })
    .catch((ex) => {
      console.error(ex);
      throw ex;
    });
};

export const getMatchedTrialsNctNumbers = (trials) => trials.map(({ nct_number }) => nct_number).sort();
